import EventEmitter from "events";
import TypedEmitter from "typed-emitter";
import { ClientEventsType, ClientEvent, ConnectionState, SdkConnectionState } from "~/modules/client/Client/ClientEvent";
import { ClientEventEmitter } from "~/modules/client/ClientEventEmitter/ClientEventEmitter";
import {
    INTERNAL_SDK_CONNECTION_STATE_CHANGED,
    InternalSdkName,
    InternalSdkConnectionState
} from "~/modules/client/ClientEventEmitter/InternalSdkEvents";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";

interface SdkEntry {
    hasEverBeenConnected: boolean;
    currentState: ConnectionState;
}

const SDK_NAMES: InternalSdkName[] = ["conversations", "voice", "taskrouter"];

export class ConnectionStateManager {
    readonly #clientEmitter: TypedEmitter<ClientEventsType>;

    readonly #clientEventEmitter: ClientEventEmitter;

    #isNetworkOnline: boolean;

    readonly #sdkStates = new Map<InternalSdkName, SdkEntry>();

    #overallState: SdkConnectionState;

    constructor(ctx: ContextManager, clientEmitter: TypedEmitter<ClientEventsType>) {
        this.#clientEmitter = clientEmitter;
        this.#clientEventEmitter = ctx.retrieveInstanceOf(ContextInstance.ClientEventEmitter, ClientEventEmitter);
        this.#isNetworkOnline = typeof navigator !== "undefined" ? navigator.onLine : true;

        for (const name of SDK_NAMES) {
            this.#sdkStates.set(name, { hasEverBeenConnected: false, currentState: "notInitialized" });
        }

        this.#overallState = {
            overall: this.#isNetworkOnline ? "connected" : "disconnected",
            network: this.#isNetworkOnline ? "connected" : "disconnected",
            conversations: "notInitialized",
            voice: "notInitialized",
            taskrouter: "notInitialized"
        };

        if (typeof window !== "undefined") {
            window.addEventListener("online", this.#onNetworkOnline);
            window.addEventListener("offline", this.#onNetworkOffline);
        }

        this.#clientEventEmitter.on(INTERNAL_SDK_CONNECTION_STATE_CHANGED, this.#onSdkConnectionStateChanged);
    }

    get connectionState(): SdkConnectionState {
        return { ...this.#overallState };
    }

    destroy(): void {
        if (typeof window !== "undefined") {
            window.removeEventListener("online", this.#onNetworkOnline);
            window.removeEventListener("offline", this.#onNetworkOffline);
        }
        this.#clientEventEmitter.removeListener(
            INTERNAL_SDK_CONNECTION_STATE_CHANGED,
            this.#onSdkConnectionStateChanged
        );
    }

    readonly #onNetworkOnline = (): void => {
        this.#isNetworkOnline = true;
        this.#recalculate();
    };

    readonly #onNetworkOffline = (): void => {
        this.#isNetworkOnline = false;
        this.#recalculate();
    };

    readonly #onSdkConnectionStateChanged = (sdkName: InternalSdkName, raw: InternalSdkConnectionState): void => {
        const existing = this.#sdkStates.get(sdkName) ?? { hasEverBeenConnected: false, currentState: "notInitialized" as ConnectionState };

        if (raw === "connected") {
            existing.hasEverBeenConnected = true;
            existing.currentState = "connected";
        } else if (raw === "disconnected") {
            existing.currentState = "disconnected";
        } else if (raw === "notInitialized") {
            existing.currentState = "notInitialized";
        } else {
            
            if (!existing.hasEverBeenConnected) {
                return;
            }
            existing.currentState = "connecting";
        }

        this.#sdkStates.set(sdkName, existing);
        this.#recalculate();
    };

    #recalculate(): void {
        const network: "connected" | "disconnected" = this.#isNetworkOnline ? "connected" : "disconnected";

        let overall: ConnectionState = "connected";

        if (!this.#isNetworkOnline) {
            overall = "disconnected";
        } else {
            for (const entry of this.#sdkStates.values()) {
                
                if (entry.currentState === "notInitialized") continue;
                if (entry.currentState === "disconnected") {
                    overall = "disconnected";
                    break;
                }
                if (entry.currentState === "connecting") {
                    overall = "connecting";
                }
            }
        }

        const conversations = this.#sdkStates.get("conversations")!.currentState;
        const voice = this.#sdkStates.get("voice")!.currentState;
        const taskrouter = this.#sdkStates.get("taskrouter")!.currentState;

        const newState: SdkConnectionState = {
            overall,
            network,
            conversations,
            voice,
            taskrouter
        };

        if (!this.#hasStateChanged(newState)) {
            return;
        }

        this.#overallState = newState;
        (this.#clientEmitter as unknown as EventEmitter).emit(ClientEvent.ConnectionStateChanged, newState);
    }

    #hasStateChanged(newState: SdkConnectionState): boolean {
        return (
            newState.overall !== this.#overallState.overall ||
            newState.network !== this.#overallState.network ||
            newState.conversations !== this.#overallState.conversations ||
            newState.voice !== this.#overallState.voice ||
            newState.taskrouter !== this.#overallState.taskrouter
        );
    }
}
